// Placeholder preview pane. Phase 2a replaces this with C's <ReadingPage>
// pointed at the proposed glossary. For now we wire `useTermCrossHighlight`
// directly against the doc fixture + the currently non-rejected proposed
// terms so the customer sees a live-marked preview while reviewing.

import { useMemo, useRef } from "react";
import { useTermCrossHighlight, type TermEntry } from "../reading";
import "../reading/reading.css";
import type { Block, DocJSON, GlossaryEntry, InlineRun } from "../data/types";
import { useReviewStore } from "./store";

interface Props {
  doc: DocJSON;
}

export function PreviewPane({ doc }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  // Live preview = every term whose current action isn't "rejected".
  // Added terms participate; pending terms do too — the customer sees
  // what their reader would see if they published now.
  const proposed = useReviewStore((s) => s.proposed);
  const added = useReviewStore((s) => s.added);
  const actions = useReviewStore((s) => s.actions);

  const previewTerms = useMemo<TermEntry[]>(() => {
    const all: GlossaryEntry[] = [...proposed, ...added];
    return all
      .filter((t) => (actions[t.term_id]?.action ?? "pending") !== "rejected")
      .map((t) => {
        const editedAlts = actions[t.term_id]?.edited_alternates ?? t.alternates;
        return { term: t.term, termId: t.term_id, alternates: editedAlts };
      });
  }, [proposed, added, actions]);

  useTermCrossHighlight(scrollRef, mainRef, previewTerms);

  return (
    <div ref={scrollRef} className="maisight-scroll maisight-review-preview">
      <main ref={mainRef} className="maisight-review-preview-main" aria-label="Document preview">
        {doc.title ? <h1 className="maisight-review-preview-title">{doc.title}</h1> : null}
        {doc.sections.map((s) => (
          <section
            key={s.id}
            data-section-id={s.id}
            className="maisight-review-preview-section"
          >
            {s.heading ? (
              <h2 className="maisight-review-preview-section-head">{s.heading}</h2>
            ) : null}
            {s.blocks.map((b) => renderBlock(b))}
          </section>
        ))}
      </main>
    </div>
  );
}

function renderBlock(block: Block) {
  if (block.kind === "paragraph") {
    return (
      <p key={block.id} data-block-id={block.id} className="maisight-review-preview-p">
        {renderInline(block.inline ?? [])}
      </p>
    );
  }
  // Other block kinds are out of scope here — the full ReadingPage handles them.
  return null;
}

function renderInline(runs: InlineRun[]) {
  return runs.map((r, i) => {
    if (r.kind === "code") return <code key={i}>{r.text}</code>;
    return <span key={i}>{r.text ?? ""}</span>;
  });
}
